import {
  HeadContent,
  Outlet,
  createRootRoute,
  lazyRouteComponent,
  type ErrorComponentProps,
} from "@tanstack/react-router";
import { useEffect, useState } from "react";

import { ConfirmDialogHost } from "../components/ConfirmDialogHost";
import { SlowRpcRequestToastCoordinator } from "../components/SlowRpcRequestToastCoordinator";
import { StandaloneSurfaceShell } from "../components/StandaloneSurfaceShell";
import { Button } from "../components/ui/button";
import { CardDescription } from "../components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../components/ui/collapsible";
import { CopyBlock } from "../components/ui/copy-block";
import { AnchoredToastProvider, ToastProvider } from "../components/ui/toast";
import { syncBrowserChromeTheme } from "../hooks/useTheme";

export const Route = createRootRoute({
  component: RootRouteView,
  errorComponent: RootRouteErrorView,
  notFoundComponent: lazyRouteComponent(
    () => import("../components/marketing/MarketingNotFoundPage"),
    "MarketingNotFoundPage",
  ),
  head: () => ({
    meta: [{ title: "Maito" }],
  }),
});

function RootRouteView() {
  useEffect(() => {
    const frame = window.requestAnimationFrame(() => {
      syncBrowserChromeTheme();
    });
    return () => {
      window.cancelAnimationFrame(frame);
    };
  }, []);

  return (
    <ToastProvider>
      <AnchoredToastProvider>
        <HeadContent />
        <ConfirmDialogHost />
        <SlowRpcRequestToastCoordinator />
        <Outlet />
      </AnchoredToastProvider>
    </ToastProvider>
  );
}

function RootRouteErrorView({ error, reset }: ErrorComponentProps) {
  const message = errorMessage(error);
  const details = errorDetails(error);
  const [detailsOpen, setDetailsOpen] = useState(false);

  return (
    <StandaloneSurfaceShell>
      <h1 className="text-2xl font-semibold tracking-tight">Something went wrong.</h1>
      <div className="mt-2">
        <CardDescription>{message}</CardDescription>
      </div>

      <div className="mt-5 flex flex-wrap gap-2">
        <Button onClick={() => reset()}>Try again</Button>
        <Button variant="secondary" onClick={() => window.location.reload()}>
          Reload app
        </Button>
      </div>

      <Collapsible className="mt-5" onOpenChange={setDetailsOpen} open={detailsOpen}>
        <CollapsibleTrigger render={<Button variant="ghost" />}>
          {detailsOpen ? "Hide error details" : "Show error details"}
        </CollapsibleTrigger>
        <CollapsibleContent className="mt-3">
          <CopyBlock label="error details" value={details} />
        </CollapsibleContent>
      </Collapsible>
    </StandaloneSurfaceShell>
  );
}

function errorMessage(error: unknown): string {
  if (error instanceof Error && error.message.trim().length > 0) {
    return error.message;
  }

  if (typeof error === "string" && error.trim().length > 0) {
    return error;
  }

  return "An unexpected router error occurred.";
}

function errorDetails(error: unknown): string {
  if (error instanceof Error) {
    return error.stack ?? error.message;
  }

  if (typeof error === "string") {
    return error;
  }

  try {
    return JSON.stringify(error, null, 2);
  } catch {
    return "No additional error details are available.";
  }
}
